import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Loader2, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import ProfawzLogo from "../components/ProfawzLogo";

export default function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) {
            setError("Le mot de passe doit faire au moins 6 caractères.");
            return;
        }
        if (password !== confirm) {
            setError("Les deux mots de passe ne correspondent pas.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setDone(true);
            setTimeout(() => navigate("/login"), 2400);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper flex flex-col">
            <header className="border-b border-rule">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
                    <ProfawzLogo size={28} />
                    <div>
                        <div className="h-display text-base font-bold">Profawz</div>
                        <div className="text-[10px] font-mono text-muted2 uppercase tracking-wider">Nouveau mot de passe</div>
                    </div>
                </div>
            </header>
            <main className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <Link to="/login" className="text-xs font-mono text-muted2 inline-flex items-center gap-1 hover:text-ink mb-4">
                        <ArrowLeft size={12} /> Retour à la connexion
                    </Link>

                    {done ? (
                        <div className="panel p-8 text-center" data-testid="reset-done">
                            <CheckCircle2 size={42} className="text-emerald-500 mx-auto mb-3" />
                            <h1 className="h-display text-2xl font-semibold">Mot de passe modifié</h1>
                            <p className="text-sm text-muted2 mt-3">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
                            <Link to="/login" className="btn-primary mt-5 inline-flex">Se connecter</Link>
                        </div>
                    ) : (
                        <>
                            <div className="label-eyebrow mb-2">Réinitialisation</div>
                            <h1 className="h-display text-3xl font-semibold mb-2">Choisissez un nouveau mot de passe</h1>
                            <p className="text-sm text-muted2 mb-6">Au moins 6 caractères. Le lien expire 1 heure après sa génération.</p>

                            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                                <div>
                                    <label className="label-eyebrow mb-2 block">Nouveau mot de passe</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2"><KeyRound size={14} /></span>
                                        <input
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="field-input !pl-9"
                                            placeholder="••••••••"
                                            required
                                            minLength={6}
                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            spellCheck="false"
                                            autoComplete="new-password"
                                            data-testid="reset-password-input"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="label-eyebrow mb-2 block">Confirmer</label>
                                    <input
                                        type="password"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        className="field-input"
                                        placeholder="••••••••"
                                        required
                                        minLength={6}
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck="false"
                                        autoComplete="new-password"
                                        data-testid="reset-confirm-input"
                                    />
                                </div>

                                {error && (
                                    <div className="text-sm text-destructive border border-destructive/20 bg-destructive/5 px-3 py-2 rounded-sm" data-testid="reset-error">
                                        {error}
                                    </div>
                                )}

                                <button type="submit" disabled={loading} className="btn-primary w-full !py-3" data-testid="reset-submit-button">
                                    {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                                    {loading ? "Mise à jour…" : "Mettre à jour mon mot de passe"}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}
